import Image from 'next/image';
import { CSSProperties, FC } from 'react';
import classNames from 'classnames';

import { Link, PrismicImageWithAlt } from '@/components';
import { PromoCarouselDocumentDataCarouselItemsItemWithID } from '@/types';
import { getContentAlign, getSpecialURL } from '@/utils';

import { getPromoCardImageSource, getPromoSpecialPresentation } from './utils';
import {
  PromoCardCta,
  PromoCardTitleDescriptionBlock,
} from './PromoCard.component';
import overlayStyle from './promocard.module.scss';
import splitStyle from './promocardsplit.module.scss';

export type PromoCardSplitProps = {
  className?: string;
  special?: PromoCarouselDocumentDataCarouselItemsItemWithID;
  specialUrl?: string;
};

const PromoCardSplit: FC<PromoCardSplitProps> = ({
  className,
  special,
  specialUrl,
}) => {
  if (!special) return null;

  const pres = getPromoSpecialPresentation(special);
  const imageSource = getPromoCardImageSource(special, pres);
  const hasInnerPadding = special.card_inner_padding ?? false;

  const resolvedSpecialUrl = specialUrl || getSpecialURL(special);

  const hasCustomCtaLink = Boolean(special.cta_link);

  const contentAlign =
    special.text_align === 'center'
      ? 'center'
      : special.text_align === 'right'
      ? 'right'
      : 'left';
  const textAlign = contentAlign as CSSProperties['textAlign'];
  const alignItems = getContentAlign(
    contentAlign,
  ) as CSSProperties['alignItems'];

  const textBlockStyle: CSSProperties = {
    alignItems,
    textAlign,
  };

  const mediaFigureClass = classNames(splitStyle.figure, {
    [splitStyle.figure_flat_bottom]: !hasInnerPadding,
    [splitStyle.figure_round_all]: hasInnerPadding,
  });

  const cardLink =
    resolvedSpecialUrl && hasCustomCtaLink ? (
      <Link
        aria-label={`See details of ${pres.titleText || 'promotion'}`}
        className={overlayStyle.card_hit_area}
        href={resolvedSpecialUrl}
      >
        <span className="sr-only">
          See details of {pres.titleText || 'promotion'}
        </span>
      </Link>
    ) : null;

  const renderCardContent = () => (
    <>
      {cardLink}
      <div className={splitStyle.inner}>
        <div
          className={classNames(splitStyle.media, {
            [splitStyle.media_padded]: hasInnerPadding,
            [splitStyle.media_picture_only]: pres.pictureOnly,
          })}
        >
          <figure className={mediaFigureClass}>
            {imageSource === 'treez' && (
              <Image alt={pres.altText} fill src={special.imageUrl!} />
            )}
            {imageSource === 'prismic' && (
              <PrismicImageWithAlt
                defaultAlt={pres.altText}
                field={special.picture}
                fill
              />
            )}
          </figure>
        </div>
        {!pres.pictureOnly && (
          <div className={splitStyle.text_block} style={textBlockStyle}>
            <PromoCardTitleDescriptionBlock
              descriptionWrapClassName={splitStyle.description}
              extraDescriptionClassName={splitStyle.extra_description}
              pres={pres}
            />
            <PromoCardCta
              ctaClassName={overlayStyle.cta_button}
              ctaHref={special.cta_link}
              ctaOutlanderClassName={overlayStyle.cta_button_outlander}
              pres={pres}
            />
          </div>
        )}
      </div>
    </>
  );

  if (!hasCustomCtaLink && resolvedSpecialUrl) {
    return (
      <Link
        aria-label={pres.cardAriaLabel}
        className={classNames(splitStyle.root, className, {
          [splitStyle.root_no_cta]: !pres.showCta,
        })}
        href={resolvedSpecialUrl}
        style={pres.cardBackgroundStyle}
      >
        {renderCardContent()}
      </Link>
    );
  }

  return (
    <div
      aria-label={pres.cardAriaLabel}
      className={classNames(splitStyle.root, className, {
        [splitStyle.root_no_cta]: !pres.showCta,
      })}
      role="group"
      style={pres.cardBackgroundStyle}
    >
      {renderCardContent()}
    </div>
  );
};

export { PromoCardSplit };
